/**
 * testDefinitionService.ts
 * PR B (qa/test-catalog-modernize-2026-07-11/PLAN.md) — frontend service for
 * the unified test-definition API (/api/admin/test-definitions, PR A).
 *
 * The "one object" UX (D1): a catalog item (commercial) and its PRIMARY task
 * type (operational) are edited as one thing. Types here MIRROR
 * backend/src/services/testDefinitionService.ts + testDefinitionTx.ts — keep
 * both sides in sync when a field is added.
 *
 * All requests go through apiClient (MSAL auth + error capture). The entire
 * backend router is admin-gated.
 */

import apiClient from './apiClient';
import type { TestCatalogItem } from '../types/testCatalogItem';

// ── Response shapes (mirror the backend service) ──────────────────────────────

export interface TestDefinitionTaskType {
  TaskTypeKey: string;
  Name: string;
  Description: string | null;
  Category: string | null;
  Subcategory: string | null;
  LabSection: string | null;
  PresetDurationDays: number;
  DefaultMinutes: number | null;
  RunMode: 'attended' | 'continuous';
  AttendedMinutes: number | null;
  SkillKey: string | null;
  DefaultMachineCount: number;
  SortOrder: number;
  IsActive: boolean;
  /** Active MachineTaskCapability rows — 0 = "Needs machine/time" amber state. */
  CapabilityCount: number;
  /** Index Tests Phase 1 / I1: admin-editable flag — pools this test on the shared virtual Index machine (tests/day) instead of minutes. */
  IsIndexTest: boolean;
  /** Index Tests Phase 1 / I4: tests/day capacity on the Index machine; only meaningful when IsIndexTest=true. */
  IndexTestsPerDay: number | null;
  /** Migration 119 (P2): baked per-test person minutes (Shawn's table) — read-only; null = no profile. */
  effortProfile?: {
    UnpackCutMinutes: number;
    GrindingMinutes: number;
    ConformityMinutes: number;
    TestMinutes: number;
    ReportMinutes: number;
    ReviewMinutes: number;
    ApproveMinutes: number;
    TotalMinutes: number;
  } | null;
}

export type MappingStatus = 'confirmed' | 'unconfirmed' | 'name-match' | 'unmapped';

/**
 * One unified row: every TestCatalogItem field (structurally a superset of
 * types/testCatalogItem.TestCatalogItem — extending keeps the prereq/
 * methodology dialogs consumable without conversion) + its primary task type
 * + derived mapping/recipe flags.
 */
export interface TestDefinitionItem extends TestCatalogItem {
  /** Same-named recipe task type, else the sole recipe task type, else null. */
  primaryTaskType: TestDefinitionTaskType | null;
  /** Active recipe rows pointing at active task types. */
  recipeSize: number;
  mappingStatus: MappingStatus;
  /** Mapped lab test name (map row preferred, else exact-name match), null if none. */
  labTestName: string | null;
  hasPrerequisites: boolean;
  hasMethodology: boolean;
}

export interface TestDefinitionsPayload {
  items: TestDefinitionItem[];
  /** Active task types in NO recipe — the operational prep steps (D3). */
  operationalTaskTypes: TestDefinitionTaskType[];
}

export interface DeleteImpact {
  TestCatalogItemKey: string;
  TestName: string;
  primaryTaskType: { TaskTypeKey: string; Name: string } | null;
  /** True when deleting would deactivate the primary task type (not shared). */
  wouldDeactivateTaskType: boolean;
  sharedRecipeCount: number;
  /** Active ScheduleTask rows referencing the primary task type. */
  liveTaskCount: number;
}

// ── Write payloads (mirror backend testDefinitionTx.ts allowlists) ───────────

export interface CatalogPatch {
  TestName?: string;
  AstmCode?: string | null;
  BstCode?: string | null;
  Category?: string | null;
  Unit?: string;
  UnitPriceCad?: number;
  UnitPriceUsd?: number | null;
  DefaultQuantity?: number;
  Description?: string | null;
  LabNote?: string | null;
  Assumptions?: string | null;
  DefaultMachineKey?: string | null;
  /** Migration 111: include this test in scenario planning (default true). */
  IncludeInSchedule?: boolean;
  /** Migration 118: machine | people (effort line, no machine) | none. The BE converges IncludeInSchedule. */
  SchedulingMode?: 'machine' | 'people' | 'none';
  IsActive?: boolean;
}

export interface TaskTypePatch {
  Name?: string;
  Description?: string | null;
  Category?: string | null;
  Subcategory?: string | null;
  LabSection?: string | null;
  PresetDurationDays?: number;
  DefaultMinutes?: number | null;
  RunMode?: 'attended' | 'continuous';
  AttendedMinutes?: number | null;
  SkillKey?: string | null;
  DefaultMachineCount?: number;
  SortOrder?: number;
  IsActive?: boolean;
  /** Index Tests Phase 1 / I1+I5: admin-editable, gated same as every other field here. */
  IsIndexTest?: boolean;
  /** Index Tests Phase 1 / I4: tests/day capacity on the shared Index machine. */
  IndexTestsPerDay?: number | null;
}

/** PUT /:catalogKey body — one transactional write for both sides (A4). */
export interface UpdateDefinitionRequest {
  catalog: CatalogPatch;
  taskType: TaskTypePatch | null;
}

/** POST / body — create catalog item (+ same-named task type + recipe, D2). */
export interface CreateDefinitionRequest {
  catalog: CatalogPatch & { TestName: string };
  /** Default true — the D2 lockstep create. */
  createTaskType: boolean;
  taskType?: TaskTypePatch;
}

export interface UpdateDefinitionResponse {
  catalog: TestCatalogItem;
  taskType: unknown | null;
}

export interface CreateDefinitionResponse {
  catalog: TestCatalogItem;
  taskType: unknown | null;
  /** True when a same-named ACTIVE task type was linked instead of created. */
  linkedExistingTaskType: boolean;
}

export interface DeleteDefinitionResponse {
  deleted: true;
  taskTypeDeactivated: boolean;
  liveTaskCount: number;
}

export interface RemoveRecipeRowResponse {
  removed: true;
  /** Live ScheduleTask rows still referencing the removed task type (FYI only). */
  liveTaskCount: number;
}

// ── Service functions ─────────────────────────────────────────────────────────

/** GET /api/admin/test-definitions — the whole page in one round trip (A3). */
export async function fetchTestDefinitions(): Promise<TestDefinitionsPayload> {
  const response = await apiClient.get<TestDefinitionsPayload>('/admin/test-definitions');
  return response.data;
}

/** PUT /api/admin/test-definitions/:catalogKey — transactional both-or-neither (A4). */
export async function updateTestDefinition(
  catalogKey: string,
  body: UpdateDefinitionRequest,
): Promise<UpdateDefinitionResponse> {
  const response = await apiClient.put<UpdateDefinitionResponse>(
    `/admin/test-definitions/${catalogKey}`,
    body,
  );
  return response.data;
}

/** POST /api/admin/test-definitions — create both sides in one transaction (A4). */
export async function createTestDefinition(
  body: CreateDefinitionRequest,
): Promise<CreateDefinitionResponse> {
  const response = await apiClient.post<CreateDefinitionResponse>(
    '/admin/test-definitions',
    body,
  );
  return response.data;
}

/** DELETE /api/admin/test-definitions/:catalogKey — soft delete + lockstep deactivate (A5). */
export async function deleteTestDefinition(
  catalogKey: string,
): Promise<DeleteDefinitionResponse> {
  const response = await apiClient.delete<DeleteDefinitionResponse>(
    `/admin/test-definitions/${catalogKey}`,
  );
  return response.data;
}

/** GET /:catalogKey/delete-impact — data for the pre-delete confirm dialog (A5). */
export async function fetchDeleteImpact(catalogKey: string): Promise<DeleteImpact> {
  const response = await apiClient.get<DeleteImpact>(
    `/admin/test-definitions/${catalogKey}/delete-impact`,
  );
  return response.data;
}

/** GET /:catalogKey/recipe — the item's recipe task types (A2). */
export async function fetchRecipe(catalogKey: string): Promise<TestDefinitionTaskType[]> {
  const response = await apiClient.get<TestDefinitionTaskType[]>(
    `/admin/test-definitions/${catalogKey}/recipe`,
  );
  return response.data;
}

/**
 * POST /:catalogKey/recipe — add a task type to the recipe (A2).
 * 409 RECIPE_ROW_EXISTS when already linked. Returns the refreshed recipe.
 */
export async function addRecipeTaskType(
  catalogKey: string,
  taskTypeKey: string,
): Promise<TestDefinitionTaskType[]> {
  const response = await apiClient.post<TestDefinitionTaskType[]>(
    `/admin/test-definitions/${catalogKey}/recipe`,
    { taskTypeKey },
  );
  return response.data;
}

/**
 * DELETE /:catalogKey/recipe/:taskTypeKey — remove a recipe row (A2).
 * 400 REMOVE_LAST_RECIPE_ROW guards the item's last active row.
 */
export async function removeRecipeTaskType(
  catalogKey: string,
  taskTypeKey: string,
): Promise<RemoveRecipeRowResponse> {
  const response = await apiClient.delete<RemoveRecipeRowResponse>(
    `/admin/test-definitions/${catalogKey}/recipe/${taskTypeKey}`,
  );
  return response.data;
}
